import { r as registerInstance, h, H as Host } from './index-662ec80e.js';
import { p as propCheck, r as runWatchers, h as hasPropError, d as displayPropError } from './propCheck-e7571e3e.js';
import { S as vOneOf, a1 as vUndefOr, a as vString, b as vMinLength, Q as vBoolean } from './validation-02b8d01d.js';
import { t as tuple } from './types-6f6b2fba.js';
import { i as iconNames } from './types-41f9e587.js';
import { c as copyToClipboard } from './browser-202fc7f4.js';
import './i18n-939e2dee.js';

const splitIconVersions = tuple('error-success', 'info-success', 'warning-success');
const splitIconSizes = tuple('xs', 'sm', 'md', 'lg');

const dsSplitTagCss = "ds-split-tag{display:-ms-flexbox;display:flex;-ms-flex-align:center;align-items:center;border-radius:200px;padding:calc(var(--gw) * 0.5) calc(var(--gw) * 1);color:var(--grey-900)}ds-split-tag span{display:-ms-inline-flexbox;display:inline-flex;-ms-flex-align:center;align-items:center;font-size:0.79012346rem;line-height:1.3rem;line-height:1}ds-split-tag span ds-icon{fill:currentColor}ds-split-tag span.split-tag__left{border-radius:200px 0 0 200px;padding:calc(var(--gw) * 0.5) calc(var(--gw) * 0.45) calc(var(--gw) * 0.5) calc(var(--gw) * 0.56)}ds-split-tag span.split-tag__left ds-icon{margin-right:calc(var(--gw) * 0.2)}ds-split-tag span.split-tag__right{background-color:var(--grey-500);border-radius:0 200px 200px 0;padding:calc(var(--gw) * 0.5) calc(var(--gw) * 0.56) calc(var(--gw) * 0.5) calc(var(--gw) * 0.45)}ds-split-tag span.split-tag__right ds-icon{margin-right:calc(var(--gw) * 0.2)}ds-split-tag[size='xs'] span.split-tag__left{padding:calc(var(--gw) * 0.4) calc(var(--gw) * 0.2) calc(var(--gw) * 0.4) calc(var(--gw) * 0.4)}ds-split-tag[size='xs'] span.split-tag__right{padding:calc(var(--gw) * 0.4) calc(var(--gw) * 0.4) calc(var(--gw) * 0.4) calc(var(--gw) * 0.2)}ds-split-tag[size='md']{}ds-split-tag[size='md'] span{font-size:0.88888889rem;line-height:1.56rem;line-height:1}ds-split-tag[size='md'] span.split-tag__left{padding:calc(var(--gw) * 0.56) calc(var(--gw) * 0.5) calc(var(--gw) * 0.56) calc(var(--gw) * 0.56)}ds-split-tag[size='md'] span.split-tag__right{padding:calc(var(--gw) * 0.56) calc(var(--gw) * 0.56) calc(var(--gw) * 0.56) calc(var(--gw) * 0.5)}ds-split-tag[size='lg']{}ds-split-tag[size='lg'] span{font-size:1rem;line-height:1.69rem;line-height:1}ds-split-tag[size='lg'] span.split-tag__left{border-radius:300px 0 0 300px;padding:calc(var(--gw) * 0.6) calc(var(--gw) * 0.55) calc(var(--gw) * 0.6) calc(var(--gw) * 1.1)}ds-split-tag[size='lg'] span.split-tag__right{border-radius:0 300px 300px 0;padding:calc(var(--gw) * 0.6) calc(var(--gw) * 1.1) calc(var(--gw) * 0.55) calc(var(--gw) * 0.6)}ds-split-tag[version='warning-success'] .split-tag__left{background-color:var(--orange-50);border:1px solid var(--orange-100);color:var(--orange-700)}ds-split-tag[version='warning-success'] .split-tag__right{background-color:var(--green-50);border:1px solid var(--green-100);color:var(--green-600)}ds-split-tag[version='error-success'] .split-tag__left{background-color:var(--red-50);border:1px solid var(--red-100);color:var(--red-600)}ds-split-tag[version='error-success'] .split-tag__right{background-color:var(--green-50);border:1px solid var(--green-100);color:var(--green-600)}ds-split-tag[version='info-success'] .split-tag__left{background-color:var(--blue-50);border:1px solid var(--blue-200);color:var(--blue-700)}ds-split-tag[version='info-success'] .split-tag__right{background-color:var(--green-50);border:1px solid var(--green-100);color:var(--green-600)}ds-split-tag[is-fixed-width]{cursor:pointer}ds-split-tag[is-fixed-width] span:hover{filter:brightness(95%)}";

const DsSplitTag = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.propError = {};
    this.version = 'error-success';
    this.size = 'sm';
    this.leftValue = undefined;
    this.rightValue = undefined;
    this.defaultValue = '-';
    this.leftIcon = undefined;
    this.rightIcon = undefined;
    this.iconSize = '12px';
    this.valueMaxWidth = '2.8rem';
    this.isFixedWidth = false;
    this.isCopyable = false;
    this.leftCopied = false;
    this.rightCopied = false;
  }
  watchVersion() {
    /* istanbul ignore if */
    if (!propCheck(this, 'version', splitIconVersions.join(' | '), vOneOf(splitIconVersions)))
      return;
  }
  watchSize() {
    /* istanbul ignore if */
    if (!propCheck(this, 'size', splitIconSizes.join(' | '), vOneOf(splitIconSizes)))
      return;
  }
  watchLeftValue() {
    /* istanbul ignore if */
    if (!propCheck(this, 'leftValue', 'string', vUndefOr(vString)))
      return;
  }
  watchRightValue() {
    /* istanbul ignore if */
    if (!propCheck(this, 'rightValue', 'string', vUndefOr(vString)))
      return;
  }
  watchDefaultValue() {
    /* istanbul ignore if */
    if (!propCheck(this, 'defaultValue', 'string', vMinLength(1)))
      return;
  }
  watchLeftIcon() {
    /* istanbul ignore if */
    if (!propCheck(this, 'leftIcon', iconNames.join(' | '), vUndefOr(vOneOf(iconNames))))
      return;
  }
  watchRightIcon() {
    /* istanbul ignore if */
    if (!propCheck(this, 'rightIcon', iconNames.join(' | '), vUndefOr(vOneOf(iconNames))))
      return;
  }
  watchIconSize() {
    /* istanbul ignore if */
    if (!propCheck(this, 'iconSize', 'string', vMinLength(1)))
      return;
  }
  watchValueMaxWidth() {
    /* istanbul ignore if */
    if (!propCheck(this, 'valueMaxWidth', 'string', vMinLength(1)))
      return;
  }
  watchIsFixedWidth() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isFixedWidth', 'boolean', vBoolean))
      return;
  }
  watchIsCopyable() {
    /* istanbul ignore if */
    if (!propCheck(this, 'isCopyable', 'boolean', vBoolean))
      return;
  }
  async copy(direction) {
    var _a;
    await copyToClipboard((_a = this[`${direction}Value`]) !== null && _a !== void 0 ? _a : this.defaultValue);
    this[`${direction}Copied`] = true;
    setTimeout(() => {
      this[`${direction}Copied`] = false;
    }, 2000);
  }
  connectedCallback() {
    runWatchers(this);
  }
  render() {
    var _a, _b;
    if (hasPropError(this))
      return displayPropError(this);
    const values = {
      left: (_a = this.leftValue) !== null && _a !== void 0 ? _a : this.defaultValue,
      right: (_b = this.rightValue) !== null && _b !== void 0 ? _b : this.defaultValue,
    };
    const dsIcon = (name) => (h("ds-icon", { name: name, size: this.iconSize }));
    const splitTag = (direction) => (h("span", { class: `split-tag__${direction}` }, this[`${direction}Icon`] && dsIcon(this[`${direction}Icon`]), this.isFixedWidth
      ? h("div", { class: "_text-overflow:ellipsis", style: { width: this.valueMaxWidth } }, values[direction])
      : values[direction]));
    const isCopyableWrapper = (direction) => {
      if (this.isCopyable)
        return (h("ds-tooltip", { text: this[`${direction}Copied`] ? 'Copied' : values[direction], onClick: () => this.copy(direction) }, splitTag(direction)));
      else
        return splitTag(direction);
    };
    return (h(Host, null, isCopyableWrapper('left'), isCopyableWrapper('right')));
  }
  static get watchers() { return {
    "version": ["watchVersion"],
    "size": ["watchSize"],
    "leftValue": ["watchLeftValue"],
    "rightValue": ["watchRightValue"],
    "defaultValue": ["watchDefaultValue"],
    "leftIcon": ["watchLeftIcon"],
    "rightIcon": ["watchRightIcon"],
    "iconSize": ["watchIconSize"],
    "valueMaxWidth": ["watchValueMaxWidth"],
    "isFixedWidth": ["watchIsFixedWidth"],
    "isCopyable": ["watchIsCopyable"]
  }; }
};
DsSplitTag.style = dsSplitTagCss;

export { DsSplitTag as ds_split_tag };
